import React, { useState, useRef, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Menu,
  MenuItem,
  Box,
  Container,
  Grid,
  Paper,
  Divider,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  RocketLaunch,
  Image,
  Palette,
  Stream,
  Movie,
  GroupWork,
  Description,
  Forum,
  Gradient,
  ZoomIn,
  FaceRetouchingNatural,
  CameraAlt,
  Expand,
} from "@mui/icons-material";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import styles from "./Navbar.module.css";
import WorkIcon from "@mui/icons-material/Work";
import MobileDrawer from "./MobileDrawer";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const menuContent = {
  features: [
    {
      icon: Image,
      title: "BG Remover",
      description: "Remove background from images instantly",
      path: "/ai-background-remover",
    },
    {
      icon: Palette,
      title: "BG Changer",
      description: "Change background colors and patterns",
      path: "/ai-background-changer",
    },
    {
      icon: Stream,
      title: "Image Enhancer",
      description: "Enhance image quality and clarity",
      path: "/ai-image-enhancer",
    },
    {
      icon: Gradient,
      title: "Image Colorizer",
      description: "Add color to black & white photos",
      path: "/ai-image-colorizer",
    },
    {
      icon: ZoomIn,
      title: "Super Resolution",
      description: "Upscale images without quality loss",
      path: "/ai-super-resolution",
    },
    {
      icon: FaceRetouchingNatural,
      title: "Face Wrap",
      description: "Advanced facial feature adjustment",
      path: "/ai-face-wrap",
    },
    {
      icon: CameraAlt,
      title: "AI Selfie Generator",
      description: "Create perfect AI-generated selfies",
      path: "/ai-selfie-generator",
    },
    {
      icon: Expand,
      title: "AI Expand Images",
      description: "Intelligent image expansion technology",
      path: "/ai-expand-images",
    },
  ],

  solutions: [
    {
      icon: Stream,
      title: "Flow State",
      description:
        "With one simple prompt, Flow State delivers a stream of visual possibilities.",
    },
    {
      icon: Movie,
      title: "AI Video Generator",
      description:
        "Turn your images into stunning animations and explore a new dimension of video storytelling.",
    },
    {
      icon: GroupWork,
      title: "Real-time Collaboration",
      description:
        "Team-based editing and version control for enterprise solutions.",
    },
  ],
  resources: [
    {
      icon: Description,
      title: "Documentation Hub",
      description: "Complete guides and API references for developers.",
    },
    {
      icon: WorkIcon,
      title: "Case Studies",
      description: "See how industry leaders are implementing our solutions.",
    },
    {
      icon: Forum,
      title: "Community Forum",
      description: "Connect with other creators and share knowledge.",
    },
  ],
};

const Navbar = () => {
  const [anchorEl, setAnchorEl] = useState({
    features: null,
    solutions: null,
    resources: null,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate("/dashboard");
  };

  const handleMenuOpen = (menuName, event) => {
    setAnchorEl((prev) => ({ ...prev, [menuName]: event.currentTarget }));
  };

  const handleMenuClose = (menuName) => {
    setAnchorEl((prev) => ({ ...prev, [menuName]: null }));
  };

  const navbarRef = useRef(null);

    // Add this useEffect hook
    useEffect(() => {
      const isOpen = Object.values(anchorEl).some(Boolean) || drawerOpen;
      document.body.classList.toggle('menu-open', isOpen);
      return () => document.body.classList.remove('menu-open');
    }, [anchorEl, drawerOpen]);

  useEffect(() => {
    gsap.to(navbarRef.current, {
      backgroundColor: "rgba(21, 11, 42, 0.95)",
      backdropFilter: "blur(10px)",
      scrollTrigger: {
        trigger: document.body,
        start: "top top",
        end: "+=500",
        scrub: 1,
      },
    });
  }, []);

  const renderMenuContent = (menuName) => (
    <Paper
      sx={{
        maxWidth: 900,
        width: "100%",
        p: 4,
        margin: "0",
        background:
          "linear-gradient(135deg, rgba(26,11,46,0.95) 0%, rgba(13,7,25,0.95) 100%)",
        color: "white",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: "16px",
        boxShadow: "0 16px 32px rgba(0,0,0,0.4)",
        overflow: "hidden",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        position: "relative",
        scrollbarWidth: "none", // Hide scrollbar for Firefox
        "&::-webkit-scrollbar": { display: "none" }, // Hide scrollbar for Chrome, Safari
      }}
    >
      <Grid container spacing={3} sx={{ py: 1 }}>
        {menuContent[menuName].map(
          ({ icon: Icon, title, description, path }, index) => (
            <Grid item xs={12} md={4} key={index} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  scrollbarWidth: "none", // Hide scrollbar for Firefox
                  "&::-webkit-scrollbar": { display: "none" }, // Hide scrollbar for Chrome, Safari
                  p: 2.5,
                  height: "100%",
                  position: "relative",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    "&:before": {
                      opacity: 1,
                    },
                  },
                  "&:before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      "linear-gradient(135deg, rgba(138,43,226,0.1) 0%, rgba(75,0,130,0.05) 100%)",
                    borderRadius: "12px",
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                    zIndex: 0,
                  },
                }}
                onClick={() => {
                  if (path) {
                    navigate(path);
                    handleMenuClose(menuName);
                  }
                }}
              >
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        "linear-gradient(45deg, #8A2BE2 30%, #4B0082 90%)",
                      borderRadius: "12px",
                      mb: 2,
                      boxShadow: "0 6px 12px rgba(138,43,226,0.3)",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                      scrollbarWidth: "none", // Hide scrollbar for Firefox
                      "&::-webkit-scrollbar": { display: "none" }, // Hide scrollbar for Chrome, Safari
                    }}
                  >
                    <Icon sx={{ color: "white", fontSize: 28 }} />
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      fontWeight: 700,
                      letterSpacing: "-0.25px",
                      background: "linear-gradient(45deg, #ffffff, #e6e6e6)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      lineHeight: 1.3,
                    }}
                  >
                    {title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      lineHeight: 1.6,
                      fontSize: "0.875rem",
                      minWidth: 0,
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                      mb: 2,
                    }}
                  >
                    {description}
                  </Typography>

                  <Divider
                    sx={{
                      mt: 3,
                      borderColor: "rgba(255,255,255,0.15)",
                      "&:after": {
                        content: '""',
                        position: "absolute",
                        right: 0,
                        width: "40%",
                        borderColor: "inherit",
                        background:
                          "linear-gradient(to right, transparent, rgba(138,43,226,0.5))",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          )
        )}
      </Grid>
    </Paper>
  );

  return (
    <AppBar position="fixed" className={styles.navbar} ref={navbarRef}>
      <Container maxWidth="xl">
        <Toolbar className={styles.toolbar}>
          <Typography variant="h4" className={styles.logo} onClick={()=>navigate("/")}>
            Remini<span className={styles.logoAccent}>.AI</span>
          </Typography>

          {!isMobile && (
            <>
              <Box className={styles.navItems}>
                {/* Features Menu */}
                <div>
                  <Button
                    className={styles.navButton}
                    onClick={(e) => handleMenuOpen("features", e)}
                    endIcon={<span className={styles.arrow}>▾</span>}
                  >
                    Features
                  </Button>
                  <Menu
                    anchorEl={anchorEl.features}
                    open={Boolean(anchorEl.features)}
                    onClose={() => handleMenuClose("features")}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    sx={{
                      '& .MuiPaper-root': {
                        scrollbarWidth: "none", // Hide scrollbar for Firefox
                        "&::-webkit-scrollbar": { display: "none" }, // Hide scrollbar for Chrome, Safari
                      }
                    }}
                    PaperProps={{
                      sx: { 
                        background: "transparent", 
                        boxShadow: "none",
                        scrollbarWidth: "none", // Hide scrollbar for Firefox
                        "&::-webkit-scrollbar": { display: "none" }, // Hide scrollbar for Chrome, Safari
                      },
                    }}
                    MenuListProps={{ sx: { p: 0 } }}
                  >
                    <MenuItem sx={{ p: 0 }}>
                      {renderMenuContent("features")}
                    </MenuItem>
                  </Menu>
                </div>

                {/* Solutions Menu */}
                <div>
                  <Button
                    className={styles.navButton}
                    onClick={(e) => handleMenuOpen("solutions", e)}
                    endIcon={<span className={styles.arrow}>▾</span>}
                  >
                    Solutions
                  </Button>
                  <Menu
                    anchorEl={anchorEl.solutions}
                    open={Boolean(anchorEl.solutions)}
                    onClose={() => handleMenuClose("solutions")}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    PaperProps={{
                      sx: { background: "transparent", boxShadow: "none" },
                    }}
                    MenuListProps={{ sx: { p: 0 } }}
                  >
                    <MenuItem sx={{ p: 0 }}>
                      {renderMenuContent("solutions")}
                    </MenuItem>
                  </Menu>
                </div>

                {/* Resources Menu */}
                <div>
                  <Button
                    className={styles.navButton}
                    onClick={(e) => handleMenuOpen("resources", e)}
                    endIcon={<span className={styles.arrow}>▾</span>}
                  >
                    Resources
                  </Button>
                  <Menu
                    anchorEl={anchorEl.resources}
                    open={Boolean(anchorEl.resources)}
                    onClose={() => handleMenuClose("resources")}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    PaperProps={{
                      sx: { background: "transparent", boxShadow: "none" },
                    }}
                    MenuListProps={{ sx: { p: 0 } }}
                  >
                    <MenuItem sx={{ p: 0 }}>
                      {renderMenuContent("resources")}
                    </MenuItem>
                  </Menu>
                </div>
                <Button className={styles.navButton} onClick={()=>navigate("/contact-us")}>Contact</Button>
              </Box>
              <Button
                variant="contained"
                className={styles.launchButton}
                onClick={handleNavigate}
              >
                Launch App
              </Button>
            </>
          )}

          {isMobile && (
            <IconButton
              color="inherit"
              onClick={() => setDrawerOpen(true)}
              sx={{ ml: "auto" }}
            >
              <MenuIcon fontSize="large" sx={{ color: "white" }} />
            </IconButton>
          )}

          <MobileDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            handleMenuOpen={handleMenuOpen}
          />
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
